import { isEmpty, isNil } from 'lodash';
import { useMemo } from 'react';
import { SubformField } from '@shared/data/entity/fields/SubformField';
import { FieldRecord } from '@shared/scenes/entity/types';
import { API } from '@shared/types/api';
import { NO_CODE_SUBFORMS, useFeatureFlag } from '@shared/util/featureFlags';
import { i18n } from '@shared/util/i18n';

export type CardEntityError = {
  description: string | null;
  componentId?: string;
  /** The inner subform component ID from a server-side COMPONENT_DATA_REQUIRED error. */
  subformComponentId?: string;
};

interface FormErrorsProps {
  apiErrors?: CardEntityError[];
  fields?: FieldRecord;
  components: API.AnyComponent[];
  includeOnSubmitErrors?: boolean;
  includeLocalErrors?: boolean;
}

interface FormErrors {
  errors: CardEntityError[];
  isValid: boolean;
}

export function useFormErrors({
  apiErrors,
  fields,
  components,
  includeOnSubmitErrors = true,
  includeLocalErrors = true,
}: FormErrorsProps): FormErrors {
  const requiredErrors = useRequiredErrors({ fields, components });
  const localValidationErrors = useLocalValidationErrors({ fields });
  // Suppress stale server errors when the user has explicitly cleared the field.
  // Only outer SubformField-slot errors (identified by a non-nil subformComponentId)
  // are suppressed this way — they correspond to a field the user can clear. Inner
  // audit-field errors (remapped via subformApiErrors, no subformComponentId) must
  // always be shown because AuditField.fromFieldJson(undefined) returns null and
  // those null values mean "empty", not "cleared by the user".
  const serverErrors = (apiErrors ?? []).filter(error => {
    if (isNil(error.componentId)) {
      return true;
    }
    if (fields?.[error.componentId] !== null) {
      return true;
    }
    // field slot is null — only suppress outer subform errors, keep inner ones.
    return isNil(error.subformComponentId);
  });

  let errors = [
    ...(includeOnSubmitErrors ? requiredErrors : []),
    ...(includeLocalErrors ? localValidationErrors : []),
    ...serverErrors,
  ];

  const hasComponentLevelErrors = errors.some(error => !isNil(error.componentId));
  const hasFormLevelErrors = errors.some(error => isNil(error.componentId));

  if (hasComponentLevelErrors && !hasFormLevelErrors && includeOnSubmitErrors) {
    errors = [...errors, { description: i18n('card.form.errorBanner'), componentId: undefined }];
  }

  return {
    errors,
    isValid: isEmpty(requiredErrors) && isEmpty(localValidationErrors),
  };
}

export function useRequiredErrors({
  fields,
  components,
}: {
  fields?: FieldRecord;
  components: API.AnyComponent[];
}): CardEntityError[] {
  return useMemo(
    () =>
      components
        .filter(component => 'required' in component && component.required === true)
        .filter(component => isNil(fields?.[component.id]) || fields?.[component.id]?.isEmpty)
        .map(component => ({
          description: i18n('card.form.errors.required'),
          componentId: component.id,
        })),
    [components, fields],
  );
}

function useLocalValidationErrors({ fields }: { fields?: FieldRecord }): CardEntityError[] {
  const isNoCodeSubforms = useFeatureFlag(NO_CODE_SUBFORMS);
  return useMemo(
    () =>
      Object.keys(fields ?? {})
        .filter(componentId => {
          const field = fields?.[componentId];
          if (field instanceof SubformField && !isNoCodeSubforms) {
            return false;
          }
          return !isNil(field?.validate());
        })
        .map(componentId => ({
          description: fields?.[componentId]?.validate() ?? '',
          componentId,
        })),
    [fields, isNoCodeSubforms],
  );
}
